import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import { Delta } from 'quill/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { SearchJobsGQL, SearchJobResult } from '../../../../../generated/graphql.generated';
import { DateTimeInputComponent } from '../../../../shared/date-time-input/date-time-input.component';
import { TimezoneHelperService } from '../../../../services/timezone-helper.service';
import { dayjs } from '../../../../core/dayjs/dayjs';

export interface OpsTaskDialogData {
    dispatchDate: Date;
    /** Existing task (edit mode) */
    existingEventId?: string;
    existing?: {
        title: string;
        notes?: string;
        jobId?: string;
        jobCode?: string;
        jobCustomerName?: string;
        status?: string;
        start: number;
        end: number;
    };
}

export interface OpsTaskDialogResult {
    title: string;
    start: number;
    end: number;
    notes: string;
    status?: string;
    jobId?: string;
    jobCode?: string;
    delete?: boolean;
    eventId?: string;
}

/** Preset time blocks (same idea as the book-off page presets). */
const TIME_PRESETS = [
    { label: 'All Day', startHour: 0, endHour: 24 },
    { label: 'Morning', startHour: 0, endHour: 12 },
    { label: 'Afternoon', startHour: 12, endHour: 24 },
    { label: 'Business Hours', startHour: 7, endHour: 17 },
    { label: 'Custom', custom: true },
];

@Component({
    standalone: true,
    selector: 'app-ops-task-dialog',
    imports: [
        CommonModule, FormsModule, ButtonModule, InputTextModule,
        DropdownModule, AutoCompleteModule, QuillEditorComponent, InputNumberModule,
        DateTimeInputComponent,
    ],
    template: `
        <div class="ops-task-dialog">
            <div class="p-fluid">
                <div class="p-field">
                    <label for="taskTitle">Task Title</label>
                    <input
                        pInputText
                        id="taskTitle"
                        type="text"
                        [(ngModel)]="title"
                        placeholder="e.g. Pick up glass, take to Allied"
                    />
                </div>
                <div class="p-field">
                    <label>Notes for the crew</label>
                    <quill-editor
                        format="object"
                        [ngModel]="notesDelta"
                        (ngModelChange)="onNotesChanged($event)"
                        [modules]="notesQuillModules"
                        [placeholder]="'e.g. Please give her a call when on the way'"
                        [compareValues]="true"
                        [styles]="{ minHeight: '90px' }"
                    ></quill-editor>
                </div>
                <div class="p-field">
                    <label>Time Block</label>
                    <p-dropdown
                        [(ngModel)]="selectedPreset"
                        [options]="timePresets"
                        optionLabel="label"
                        (onChange)="onPresetChanged()"
                        appendTo="body"
                        [style]="{ width: '100%' }"
                    >
                        <ng-template let-item pTemplate="item">
                            <div>
                                <span>{{ item.label }}</span>
                                <br />
                                <small *ngIf="!item.custom">
                                    {{ formatHours(item.startHour) }} - {{ formatHours(item.endHour) }}
                                </small>
                            </div>
                        </ng-template>
                    </p-dropdown>
                    <div class="time-row" *ngIf="selectedPreset?.custom">
                        <app-date-time-input
                            [(ngModel)]="startTimeStr"
                            [timeOnly]="true"
                            timeLabel="Start"
                            (ngModelChange)="onTypedTimeChanged()"
                        ></app-date-time-input>
                        <app-date-time-input
                            [(ngModel)]="endTimeStr"
                            [timeOnly]="true"
                            timeLabel="End"
                            (ngModelChange)="onTypedTimeChanged()"
                        ></app-date-time-input>
                    </div>
                </div>
                <div class="p-field">
                    <label>Associated job (optional)</label>
                    <ng-container *ngIf="!selectedJob; else selectedJobCard">
                        <p-autoComplete
                            [(ngModel)]="jobQuery"
                            [suggestions]="jobResults"
                            (completeMethod)="searchJobs($event)"
                            (onSelect)="onJobSelected($event)"
                            (onClear)="onJobSearchCleared()"
                            field="code"
                            [minLength]="1"
                            [delay]="300"
                            appendTo="body"
                            placeholder="Search job code or customer..."
                            [multiple]="false"
                        >
                            <ng-template let-job pTemplate="item">
                                <div class="job-search-item">
                                    <b>{{ job.code }}</b>
                                    <small class="p-ml-2">{{ job.customerName }}</small>
                                    <small class="p-ml-2 p-text-secondary" *ngIf="job.timeline">{{ job.timeline | date: 'MMM d' }}</small>
                                </div>
                            </ng-template>
                        </p-autoComplete>
                    </ng-container>
                    <ng-template #selectedJobCard>
                        <div class="selected-job-card">
                            <i class="pi pi-briefcase"></i>
                            <div class="selected-job-info">
                                <div class="selected-job-code">
                                    {{ selectedJob.code }}
                                    <small *ngIf="selectedJob.customerName" class="p-ml-2">{{ selectedJob.customerName }}</small>
                                </div>
                                <small class="p-text-secondary">Task will show on this job's page</small>
                            </div>
                            <p-button
                                icon="pi pi-times"
                                [rounded]="true"
                                [text]="true"
                                severity="secondary"
                                size="small"
                                (onClick)="clearSelectedJob()"
                                pTooltip="Remove this job"
                                tooltipPosition="left"
                            ></p-button>
                        </div>
                    </ng-template>
                </div>
            </div>
            <div class="p-d-flex p-jc-between p-mt-3">
                <p-button
                    *ngIf="data.existingEventId"
                    label="Delete"
                    icon="pi pi-trash"
                    severity="danger"
                    [outlined]="true"
                    (onClick)="onDelete()"
                ></p-button>
                <div class="p-d-flex">
                    <p-button
                        label="Cancel"
                        [outlined]="true"
                        (onClick)="ref.close()"
                        styleClass="p-mr-2"
                    ></p-button>
                    <p-button
                        label="OK"
                        (onClick)="onSave()"
                    ></p-button>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .ops-task-dialog {
            padding: 1rem;
        }
        .p-field {
            margin-bottom: 0.75rem;
        }
        label {
            display: block;
            margin-bottom: 0.25rem;
            font-size: 0.85rem;
        }
        .time-row {
            display: flex;
            gap: 0.75rem;
            margin-top: 0.5rem;
            align-items: flex-end;
        }
        .job-search-item {
            display: flex;
            align-items: baseline;
        }
        .selected-job-card {
            display: flex;
            align-items: center;
            gap: 0.6rem;
            border: 1px solid var(--surface-border, #e5e7eb);
            border-radius: 6px;
            padding: 0.5rem 0.6rem;
            background: var(--surface-50, rgba(0,0,0,0.02));
            .pi-briefcase { color: var(--primary-color, #3b82f6); }
            .selected-job-info { flex: 1 1 auto; min-width: 0; }
            .selected-job-code { font-weight: 600; font-size: 0.9rem; }
        }
        quill-editor {
            display: block;
        }
    `],
})
export class OpsTaskDialogComponent implements OnDestroy {
    public title: string = '';
    public selectedJob: SearchJobResult | null = null;
    /** Quill delta of the crew notes — same format as the job/dispatch editors. */
    public notesDelta: Delta = new Delta();
    public hasNotesDelta = false;

    /** Selected time preset (All Day / Morning / ... / Custom). */
    public timePresets = TIME_PRESETS;
    public selectedPreset: any = TIME_PRESETS[3];
    /** Typed custom times ("2:30 PM" / "14:30" style) when Custom is chosen. */
    public startTimeStr: string | null = null;
    public endTimeStr: string | null = null;

    public jobQuery: string = '';

    public data: OpsTaskDialogData;

    /** Zone timezone (same convention the dispatch board formats in). */
    private zoneTz: string;

    /** Debounced serialization of the Quill editor into notesDelta. */
    private notesSubject = new Subject<Delta>();
    private subs = new SubSink();

    /** No toolbar in the dialog — keyboard shortcuts only. */
    public notesQuillModules: QuillModules = { toolbar: null };

    public jobResults: SearchJobResult[] = [];
    private jobSearchRequestId = 0;

    constructor(
        public ref: DynamicDialogRef,
        private config: DynamicDialogConfig,
        private searchJobsGQL: SearchJobsGQL,
        private timezoneHelper: TimezoneHelperService,
    ) {
        this.data = (config?.data as OpsTaskDialogData) || {
            dispatchDate: new Date(),
        };
        this.zoneTz = this.timezoneHelper.getCurrentTimezone();

        const existing = this.data.existing;
        let initialStart: number;
        let initialEnd: number;
        if (existing) {
            this.title = existing.title || '';
            initialStart = existing.start || dayjs(this.data.dispatchDate).startOf('day').add(7, 'hours').unix();
            initialEnd = existing.end || (existing.start ?? 0) + 2 * 3600;
            this.jobQuery = existing.jobCode || '';
            if (existing.jobId && existing.jobCode) {
                // Rehydrate the selected-job card from metadata
                this.selectedJob = {
                    id: existing.jobId,
                    code: existing.jobCode,
                    customerName: existing.jobCustomerName || '',
                } as any;
            }
        } else {
            // Zone-tz default window (7-9 AM) so the dialog agrees with the board
            const day = dayjs.tz(dayjs(this.data.dispatchDate).format('YYYY-MM-DD'), this.zoneTz);
            initialStart = day.add(7, 'hour').unix();
            initialEnd = day.add(9, 'hour').unix();
        }

        this.syncPresetToTimes(initialStart, initialEnd);

        if (existing?.notes) {
            try {
                const delta = typeof existing.notes === 'string' ? JSON.parse(existing.notes) : existing.notes;
                if (delta?.ops) {
                    this.notesDelta = new Delta(delta.ops);
                    this.hasNotesDelta = true;
                }
            } catch {
                // Legacy plain-text description — wrap it so the words survive.
                this.notesDelta = new Delta().insert(existing.notes);
                this.hasNotesDelta = true;
            }
        }

        this.subs.sink = this.notesSubject.pipe(debounceTime(300)).subscribe((delta) => {
            this.notesDelta = delta;
            this.hasNotesDelta = true;
        });
    }

    /** Pick the preset matching the event's times (falls back to Custom). */
    private syncPresetToTimes(startUnix: number, endUnix: number): void {
        // Interpret in the zone tz so preset auto-detection matches the times
        // the dialog saves.
        const start = dayjs.tz(startUnix * 1000, this.zoneTz);
        const day = start.startOf('day');
        const end = dayjs.tz(endUnix * 1000, this.zoneTz);
        const sameDay = end.startOf('day').unix() === day.unix();
        const startH = start.hour();
        const endH = sameDay
            ? (end.hour() === 0 && end.minute() === 0 ? 24 : end.hour())
            : end.hour() + 24;

        const match = this.timePresets.find((p) => !p.custom
            && p.startHour === startH
            && p.endHour === (sameDay
                ? (endH === 0 && end.minute() === 0 ? 24 : endH)
                : endH));
        if (match) {
            this.selectedPreset = match;
        } else {
            this.selectedPreset = this.timePresets.find((p) => p.custom);
            this.startTimeStr = start.format('hh:mm A');
            this.endTimeStr = end.format('hh:mm A');
        }
    }

    public formatHours(hour: number): string {
        const h = hour % 24;
        const ampm = h < 12 ? 'AM' : 'PM';
        const display = h % 12 === 0 ? 12 : h % 12;
        return `${display}:00 ${ampm}`;
    }

    public onPresetChanged(): void {
        if (!this.selectedPreset?.custom) { return; }
        // Default custom times to the business-hours window so the fields
        // are never empty.
        if (!this.startTimeStr) { this.startTimeStr = '7:00 AM'; }
        if (!this.endTimeStr) { this.endTimeStr = '5:00 PM'; }
    }

    /** Typed times changed — nothing to enforce here; onSave validates. */
    public onTypedTimeChanged(): void { }

    public onNotesChanged(delta: Delta): void {
        this.notesSubject.next(delta);
    }

    public onJobSearchCleared(): void {
        ++this.jobSearchRequestId;
        this.jobResults = [];
    }

    public searchJobs($event: { query: string }): void {
        const term = $event?.query?.trim();
        if (!term) return;
        const requestId = ++this.jobSearchRequestId;
        this.searchJobsGQL.fetch({ search: term, limit: 8 }, { fetchPolicy: 'network-only' })
            .subscribe({
                next: ({ data }) => {
                    if (requestId !== this.jobSearchRequestId) return;
                    this.jobResults = data?.searchJobs?.results || [];
                },
                error: (err) => {
                    if (requestId !== this.jobSearchRequestId) return;
                    console.error('Failed to search jobs', err);
                    this.jobResults = [];
                },
            });
    }

    public onJobSelected($event: any): void {
        this.selectedJob = ($event?.value ?? $event) as SearchJobResult;
        this.jobQuery = '';
    }

    public clearSelectedJob(): void {
        this.selectedJob = null;
    }

    /** Resolve the currently chosen preset / typed times to unix seconds. */
    private resolveTimes(): { start: number; end: number } | null {
        // Resolve in the ZONE timezone (the same convention the dispatch board
        // and its day windows use), not the browser's local tz — a dispatcher
        // in a different tz than the zone would otherwise shift every time by
        // the tz difference.
        const day = dayjs.tz(dayjs(this.data.dispatchDate).format('YYYY-MM-DD'), this.zoneTz);

        if (!this.selectedPreset?.custom) {
            // endHour is an hour-of-day (0-24), NOT a day offset — adding it
            // as 'days' produced end dates weeks out (Business Hours 7-17
            // landed on day+17). 24 means midnight at the END of the day.
            const start = day.add(this.selectedPreset.startHour, 'hour');
            const end = this.selectedPreset.endHour >= 24
                ? day.endOf('day')
                : day.add(this.selectedPreset.endHour, 'hour');
            return { start: start.unix(), end: end.unix() };
        }

        // dayjs.tz(str, format, timezone) — the 2-arg form treats the second
        // argument as a timezone, not a format. The parsed time has NO date
        // (defaults to today) — anchor its clock to the viewed day, otherwise
        // an OPS task for yesterday resolves onto today.
        const start = dayjs.tz(this.startTimeStr, 'hh:mm A', this.zoneTz);
        const end = dayjs.tz(this.endTimeStr, 'hh:mm A', this.zoneTz);
        if (!start.isValid() || !end.isValid()) { return null; }

        const startAt = day
            .hour(start.hour())
            .minute(start.minute())
            .second(0);
        let endAt = day
            .hour(end.hour())
            .minute(end.minute())
            .second(0);
        if (endAt.unix() <= startAt.unix()) {
            // End at/before start — treat as next-day (overnight)
            endAt = endAt.add(1, 'day');
        }
        return { start: startAt.unix(), end: endAt.unix() };
    }

    public onSave(): void {
        const times = this.resolveTimes();
        if (!times) { return; }

        this.ref.close({
            title: this.title,
            start: times.start,
            end: times.end,
            notes: JSON.stringify(this.notesDelta),
            jobId: (this.selectedJob as any)?.id || undefined,
            jobCode: (this.selectedJob as any)?.code || undefined,
        });
    }

    public onDelete(): void {
        this.ref.close({ delete: true, eventId: this.data.existingEventId });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}